import { collection, query, where, getDocs, type DocumentData } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { getOrganizationMembers } from './getOrganizationMembers';
import { getCustomersExams } from '@/lib/firebase/queries/exams/getExams';
import { loadClinicalSummaries } from '@/lib/firebase/queries/namatAssessment';
import { summarizeClinical, type BeneficiarySummary, type OrgClinicalStats } from '@/lib/assessment/orgClinicalStats';
import { isActiveBeneficiary } from '@/lib/competitions/competitionRanking';
import { CHUNK_CONCURRENCY, chunkIds, forEachLimited } from '@/lib/firebase/queries/batchedIn';
import type { HabitCategory } from '@/types/habits';

export interface OrganizationStats {
  totalCustomers: number;
  activeCustomers: number;
  completedExams: number;
  pendingExams: number;
  averageNamatScore: number;
  totalCompetitionPoints: number;
  healthDistribution: {
    excellent: number;
    veryGood: number;
    good: number;
    needsImprovement: number;
  };
  /** Organization totals from the assessment module: exams, scale, indicators (#95). */
  clinical: OrgClinicalStats;
  monthlyTrends: Array<{
    month: string;
    users: number;
    exams: number;
    avgScore: number;
  }>;
  engagementMetrics: {
    totalHabitLogs: number;
    averagePointsPerCustomer: number;
    activeInLast30Days: number;
  };
  habitLogsByCategory: {
    Exercises: number;
    Nutrition: number;
    Stress: number;
    Sleep: number;
    Relations: number;
    Addictions: number;
  };
  habitBuildingDistribution: {
    consistent: number; // >80% adherence - Strong habit builders
    regular: number; // 60-80% adherence - Regular habit builders
    occasional: number; // 40-60% adherence - Occasional habit builders
    struggling: number; // <40% adherence - Struggling with habits
  };
}

export interface CustomerScoreData {
  userId: string;
  namatScore?: number;
  competitionPoints?: number;
  lastExamDate?: Date;
  examStatus?: string;
  groupIds?: string[];
}

export interface CompletedLogEntry {
  userId: string;
  habitId: string;
  date: string; // "YYYY-MM-DD"
  habitCategory?: string;
}

/**
 * Pure: normalize a habitLogs/competitionsLogs doc into a completed-log entry.
 * Both collections store `date` as a "YYYY-MM-DD" string (see queries/habits.ts
 * logHabit / queries/competitions.ts logCompetitionHabit); the Timestamp fallbacks
 * cover docs that lack the string date. Returns null when userId/habitId/a
 * derivable date is missing, so malformed docs are silently dropped rather than
 * corrupting the merge.
 */
export function toCompletedLogEntry(data: DocumentData | undefined): CompletedLogEntry | null {
  const userId = data?.userId;
  const habitId = data?.habitId;
  let dayKey: string | null = null;
  if (typeof data?.date === 'string' && data.date) {
    dayKey = data.date.slice(0, 10);
  } else if (data?.date?.toDate) {
    dayKey = data.date.toDate().toISOString().split('T')[0];
  } else if (data?.timestamp?.toDate) {
    dayKey = data.timestamp.toDate().toISOString().split('T')[0];
  }
  if (!userId || !habitId || !dayKey) return null;
  return { userId, habitId, date: dayKey, habitCategory: data.habitCategory };
}

/**
 * Pure: union personal habitLogs entries with competitionsLogs entries, deduped on
 * the userId+habitId+date triple so one real-world action logged through both paths
 * (a personal habit completed inside a competition) counts once toward a manager's
 * statistics. `personal` is listed first, so on the rare case where the same triple
 * carries a different category in each collection, the personal log's category wins.
 */
export function mergeCompletedLogs(
  personal: CompletedLogEntry[],
  competition: CompletedLogEntry[]
): CompletedLogEntry[] {
  const seen = new Set<string>();
  const merged: CompletedLogEntry[] = [];
  for (const entry of [...personal, ...competition]) {
    const key = `${entry.userId}|${entry.habitId}|${entry.date}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(entry);
  }
  return merged;
}

/** Pure: count of distinct calendar days across a deduped set of completed-log entries. */
export function countDistinctDays(entries: CompletedLogEntry[]): number {
  return new Set(entries.map((e) => e.date)).size;
}

/** Pure: tally completed-log entries by habitCategory, ignoring unknown/missing categories. */
export function countByCategory(entries: CompletedLogEntry[]): {
  Exercises: number; Nutrition: number; Stress: number; Sleep: number; Relations: number; Addictions: number;
} {
  const counts = { Exercises: 0, Nutrition: 0, Stress: 0, Sleep: 0, Relations: 0, Addictions: 0 };
  entries.forEach((entry) => {
    const category = entry.habitCategory as HabitCategory | undefined;
    if (category && Object.prototype.hasOwnProperty.call(counts, category)) {
      counts[category] += 1;
    }
  });
  return counts;
}

/**
 * Pure: each user's habit points (#70): one per distinct day with any
 * completed habit, personal habitLogs unioned with competitionsLogs (deduped,
 * see mergeCompletedLogs), so a habit logged from inside a competition still
 * counts.
 */
export function habitPointsByUser(
  personal: CompletedLogEntry[],
  competition: CompletedLogEntry[]
): Map<string, number> {
  const days = new Map<string, Set<string>>();
  for (const entry of mergeCompletedLogs(personal, competition)) {
    const set = days.get(entry.userId) ?? new Set<string>();
    set.add(entry.date);
    days.set(entry.userId, set);
  }
  return new Map(Array.from(days, ([userId, set]) => [userId, set.size]));
}

const POINTS_IN_CHUNK = 30; // Firestore 'in' limit

/**
 * Habit points for a roster (#70, manager beneficiaries table): two batched
 * `in` queries per 30 users instead of two queries per user. Users with no
 * completed log get 0. A failed chunk is logged and its users are left out
 * of the map (unknown), not reported as 0. competitionsLogs is filtered on
 * userId only and `completed` is checked in memory: that collection has no
 * (userId, completed) composite index.
 */
export async function getCustomersHabitPoints(userIds: string[]): Promise<Map<string, number>> {
  const ids = Array.from(new Set(userIds)).filter(Boolean);
  const out = new Map<string, number>();
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += POINTS_IN_CHUNK) chunks.push(ids.slice(i, i + POINTS_IN_CHUNK));

  await Promise.all(chunks.map(async (part) => {
    const toEntries = (docs: DocumentData[]) =>
      docs.map((d) => toCompletedLogEntry(d)).filter((e): e is CompletedLogEntry => e !== null);
    try {
      const [personalSnap, competitionSnap] = await Promise.all([
        getDocs(query(collection(db, 'habitLogs'), where('userId', 'in', part), where('completed', '==', true))),
        getDocs(query(collection(db, 'competitionsLogs'), where('userId', 'in', part))),
      ]);
      const personal = toEntries(personalSnap.docs.map((d) => d.data()));
      const competition = toEntries(competitionSnap.docs.map((d) => d.data()).filter((d) => d.completed === true));
      const points = habitPointsByUser(personal, competition);
      part.forEach((uid) => out.set(uid, points.get(uid) ?? 0));
    } catch (error) {
      console.error('Error getting habit points for a roster chunk:', error);
    }
  }));
  return out;
}

// Fetch habit logs by category for organization customers, unioning personal
// habitLogs with competitionsLogs so a habit logged inside a competition still
// counts toward the category chart. Batched by 10 (Firestore 'in' limit); each
// collection's batch query fails independently and is logged+skipped rather than
// failing the whole aggregation. Like getCustomersHabitPoints, the
// competitionsLogs batch query filters userId only ('in' is still a single-field
// filter) and checks completed in memory to avoid relying on a composite index
// that doesn't exist for this collection.
async function getHabitLogsByCategory(customerIds: string[]): Promise<{ Exercises: number; Nutrition: number; Stress: number; Sleep: number; Relations: number; Addictions: number; }> {
  if (customerIds.length === 0) {
    return {
      Exercises: 0,
      Nutrition: 0,
      Stress: 0,
      Sleep: 0,
      Relations: 0,
      Addictions: 0
    };
  }

  const habitLogsByCategory = {
    Exercises: 0,
    Nutrition: 0,
    Stress: 0,
    Sleep: 0,
    Relations: 0,
    Addictions: 0
  };

  // Process customers in batches due to Firestore 'in' limit of 10
  for (let i = 0; i < customerIds.length; i += 10) {
    const batch = customerIds.slice(i, i + 10);
    let personal: CompletedLogEntry[] = [];
    let competition: CompletedLogEntry[] = [];
    try {
      const logsQuery = query(
        collection(db, 'habitLogs'),
        where('userId', 'in', batch),
        where('completed', '==', true)
      );
      const logsSnapshot = await getDocs(logsQuery);
      personal = logsSnapshot.docs
        .map((d) => toCompletedLogEntry(d.data()))
        .filter((e): e is CompletedLogEntry => e !== null);
    } catch (error) {
      console.error('Error getting habit logs by category (habitLogs batch):', error);
    }
    try {
      const competitionLogsQuery = query(
        collection(db, 'competitionsLogs'),
        where('userId', 'in', batch)
      );
      const competitionSnapshot = await getDocs(competitionLogsQuery);
      competition = competitionSnapshot.docs
        .map((d) => d.data())
        .filter((data) => data.completed === true)
        .map((data) => toCompletedLogEntry(data))
        .filter((e): e is CompletedLogEntry => e !== null);
    } catch (error) {
      console.error('Error getting habit logs by category (competitionsLogs batch):', error);
    }

    const merged = mergeCompletedLogs(personal, competition);
    const batchCounts = countByCategory(merged);
    (Object.keys(batchCounts) as (keyof typeof habitLogsByCategory)[]).forEach((k) => {
      habitLogsByCategory[k] += batchCounts[k];
    });
  }

  console.log('📈 Habit logs by category:', habitLogsByCategory);
  return habitLogsByCategory;
}

// Calculate habit building adherence distribution for organization customers
async function getHabitBuildingDistribution(customerIds: string[]): Promise<{ consistent: number; regular: number; occasional: number; struggling: number; }> {
  try {
    if (customerIds.length === 0) {
      return { consistent: 0, regular: 0, occasional: 0, struggling: 0 };
    }

    const customerAdherence: { [customerId: string]: number[] } = {};
    
    // Initialize customer adherence tracking
    customerIds.forEach(id => {
      customerAdherence[id] = [];
    });

    // Habits and their completed logs per 30 customers (#117): one habits
    // query and one habitLogs query per chunk, instead of one habitLogs query
    // per habit. Each habit's count is the number of its completed logs, as
    // the per-habit `userId == && habitId == && completed == true` query gave.
    await forEachLimited(chunkIds(customerIds), CHUNK_CONCURRENCY, async (batch) => {
      // Get all habits for this batch of customers
      const habitsQuery = query(
        collection(db, 'habits'),
        where('userId', 'in', batch)
      );
      const habitsSnapshot = await getDocs(habitsQuery);

      // Only consider habits that have been active for at least 3 days to get meaningful data
      const habits = habitsSnapshot.docs
        .map((habitDoc) => {
          const habitData = habitDoc.data();
          const startDate = habitData.createdAt?.toDate() || new Date(habitData.startDate);
          // Calculate days passed since habit started
          const now = new Date();
          const daysPassed = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          return { userId: habitData.userId, habitId: habitDoc.id, daysPassed };
        })
        .filter((habit) => habit.daysPassed >= 3);
      if (habits.length === 0) return;

      // Completed logs of the customers who have such a habit, counted per habit
      const logsQuery = query(
        collection(db, 'habitLogs'),
        where('userId', 'in', Array.from(new Set(habits.map((habit) => habit.userId)))),
        where('completed', '==', true)
      );
      const logsSnapshot = await getDocs(logsQuery);
      const logCounts = new Map<string, number>();
      logsSnapshot.docs.forEach((logDoc) => {
        const key = `${logDoc.data().userId}|${logDoc.data().habitId}`;
        logCounts.set(key, (logCounts.get(key) ?? 0) + 1);
      });

      for (const { userId, habitId, daysPassed } of habits) {
        // Calculate adherence percentage for this habit
        const daysLogged = logCounts.get(`${userId}|${habitId}`) ?? 0;
        const adherencePercentage = Math.min(100, (daysLogged / daysPassed) * 100);

        // Add to customer's adherence scores
        if (customerAdherence[userId]) {
          customerAdherence[userId].push(adherencePercentage);
        }
      }
    });

    // Calculate distribution
    const distribution = { consistent: 0, regular: 0, occasional: 0, struggling: 0 };
    
    customerIds.forEach(customerId => {
      const adherenceScores = customerAdherence[customerId];
      
      if (adherenceScores.length > 0) {
        // Calculate average adherence for this customer
        const averageAdherence = adherenceScores.reduce((sum, score) => sum + score, 0) / adherenceScores.length;
        
        // Categorize customer based on average adherence
        if (averageAdherence >= 80) {
          distribution.consistent++;
        } else if (averageAdherence >= 60) {
          distribution.regular++;
        } else if (averageAdherence >= 40) {
          distribution.occasional++;
        } else {
          distribution.struggling++;
        }
      } else {
        // Customers with no habits are categorized as struggling
        distribution.struggling++;
      }
    });
    
    console.log('🎯 Habit building distribution:', distribution, {
      totalCustomersEvaluated: customerIds.length,
      customersWithHabits: Object.values(customerAdherence).filter(scores => scores.length > 0).length
    });
    return distribution;
  } catch (error) {
    console.error('Error getting habit building distribution:', error);
    return { consistent: 0, regular: 0, occasional: 0, struggling: 0 };
  }
}

export async function getOrganizationStatistics(organizationId: string): Promise<OrganizationStats> {
  try {
    console.log('📊 Fetching organization statistics for:', organizationId);
    
    // Get all organization members
    const members = await getOrganizationMembers(organizationId);
    // Deleted or deactivated accounts are not beneficiaries (#87).
    const customers = members.filter(member => member.role === 'customer' && isActiveBeneficiary(member));

    console.log('👥Organization members breakdown:', {
      total: members.length,
      customers: customers.length,
      managers: members.filter(m => m.role === 'manager').length,
      others: members.filter(m => m.role !== 'customer' && m.role !== 'manager').length
    });
    console.log('📅 Sample customer addedAt dates:', customers.slice(0, 3).map(c => ({ id: c.id, addedAt: c.addedAt })));
    
    if (customers.length === 0) {
      console.log('⚠️ No customers found - all statistics will be zero');
      return {
        totalCustomers: 0,
        activeCustomers: 0,
        completedExams: 0,
        pendingExams: 0,
        averageNamatScore: 0,
        totalCompetitionPoints: 0,
        healthDistribution: { excellent: 0, veryGood: 0, good: 0, needsImprovement: 0 },
        clinical: summarizeClinical([]),
        monthlyTrends: [],
        engagementMetrics: { totalHabitLogs: 0, averagePointsPerCustomer: 0, activeInLast30Days: 0 },
        habitLogsByCategory: { Exercises: 0, Nutrition: 0, Stress: 0, Sleep: 0, Relations: 0, Addictions: 0 },
        habitBuildingDistribution: { consistent: 0, regular: 0, occasional: 0, struggling: 0 }
      };
    }
    
    // Initialize counters
    let completedExams = 0;
    let pendingExams = 0;
let totalCompetitionPoints = 0;
let activeCustomers = 0;
    
    const healthDistribution = {
      excellent: 0,
      veryGood: 0,
      good: 0,
      needsImprovement: 0
    };
    
    // Each beneficiary's scale and latest indicators, summed at the end (#95).
    const summaries: BeneficiarySummary[] = [];
    // Everyone's scale and indicators in batched reads (3 queries per 30, #73).
    const summariesById = loadClinicalSummaries(customers.map((customer) => customer.id));
    // Exams and habit points in batched reads too (#117): 1 + 2 queries per 30.
    const examsById = getCustomersExams(customers.map((customer) => customer.id));
    const pointsById = getCustomersHabitPoints(customers.map((customer) => customer.id));

    // Process each customer
    const customerData: CustomerScoreData[] = await Promise.all(
      customers.map(async (customer) => {
        try {
          // Get customer exams and competition points
          const [exams, competitionPoints, summary] = await Promise.all([
            examsById.then((byId) => {
              const found = byId.get(customer.id);
              // Missing = its chunk failed, where getCustomerExams threw.
              if (!found) throw new Error('exams not loaded');
              return found;
            }),
            // Missing = its chunk failed; counted as 0 points.
            pointsById.then((byId) => byId.get(customer.id) ?? 0),
            summariesById.then((byId) => byId.get(customer.id))
          ]);
          // Missing = its chunk failed to load: left out of the totals.
          if (summary) summaries.push(summary);

          totalCompetitionPoints += competitionPoints;
          
          // The Namat scale only when valid (spec #86), as in the manager table.
          const namatScore: number | undefined =
            summary?.assessment.status === 'valid' ? summary.assessment.scale?.percent : undefined;
          let lastExamDate: Date | undefined;
          let examStatus: string | undefined;
          
          if (exams && exams.length > 0) {
            // Separate exams by status
            const completedCustomerExams = exams.filter(e => e.status === 'completed');
            const pendingCustomerExams = exams.filter(e => e.status === 'pending');
            
            // Count each exam type
            completedExams += completedCustomerExams.length;
            pendingExams += pendingCustomerExams.length;
            
            // Latest completed exam: its date for the monthly trends.
            if (completedCustomerExams.length > 0) {
              const latestExam = completedCustomerExams.sort((a, b) =>
                b.date.getTime() - a.date.getTime()
              )[0];
              lastExamDate = latestExam.date;
              examStatus = latestExam.status;
            } else {
              // Has exams but none completed - get latest for date tracking
              const latestExam = exams.sort((a, b) => 
                b.date.getTime() - a.date.getTime()
              )[0];
              lastExamDate = latestExam.date;
              examStatus = latestExam.status;
            }
          }
          
          // Consider active if has activity in last 30 days
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          const hasRecentExam = lastExamDate && lastExamDate > thirtyDaysAgo;
          const hasRecentHabits = competitionPoints > 0; // This could be refined to check recent habit logs
          
          if (hasRecentExam || hasRecentHabits) {
            activeCustomers++;
          }
          
          return {
            userId: customer.id,
            namatScore,
            competitionPoints,
            lastExamDate,
            examStatus
          };
        } catch (error) {
          console.error('Error processing customer:', customer.id, error);
          return {
            userId: customer.id,
            competitionPoints: 0
          };
        }
      })
    );
    
    // Calculate monthly trends (last 6 months)
    const monthlyTrends = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = months[monthDate.getMonth()];
      
      // Count customers who joined this month (only count if addedAt exists)
      const monthCustomers = customers.filter(customer => {
        if (customer.addedAt && customer.addedAt instanceof Date) {
          return customer.addedAt.getFullYear() === monthDate.getFullYear() && 
                 customer.addedAt.getMonth() === monthDate.getMonth();
        }
        return false; // Don't count customers without join dates
      }).length;
      
      // Count exams completed this month
      const monthExams = customerData.reduce((count, customer) => {
        if (customer.lastExamDate && 
            customer.lastExamDate.getFullYear() === monthDate.getFullYear() &&
            customer.lastExamDate.getMonth() === monthDate.getMonth()) {
          return count + 1;
        }
        return count;
      }, 0);
      
      // Average score for this month
      const monthScores = customerData
        .filter(customer => 
          customer.lastExamDate && 
          customer.lastExamDate.getFullYear() === monthDate.getFullYear() &&
          customer.lastExamDate.getMonth() === monthDate.getMonth() &&
          customer.namatScore !== undefined
        )
        .map(customer => customer.namatScore!);
      
      const avgScore = monthScores.length > 0 
        ? Math.round(monthScores.reduce((sum, score) => sum + score, 0) / monthScores.length)
        : 0;
      
      monthlyTrends.push({
        month: monthName,
        users: monthCustomers,
        exams: monthExams,
        avgScore
      });
    }

    // Get habit data for all customers
    const customerIds = customers.map(c => c.id);
    const [habitLogsByCategory, habitBuildingDistribution] = await Promise.all([
      getHabitLogsByCategory(customerIds),
      getHabitBuildingDistribution(customerIds)
    ]);
    
    console.log('📈 Monthly trends data:', monthlyTrends);
    // Organization totals from the assessment module (#95)
    const clinical = summarizeClinical(summaries);
    healthDistribution.excellent = clinical.bands.excellent;
    healthDistribution.veryGood = clinical.bands.very_good;
    healthDistribution.good = clinical.bands.good;
    healthDistribution.needsImprovement = clinical.bands.needs_improvement;

    // Calculate final statistics
const stats: OrganizationStats = {
      totalCustomers: customers.length,
      activeCustomers,
      completedExams,
      pendingExams,
      averageNamatScore: clinical.averagePercent ?? 0,
      totalCompetitionPoints,
      healthDistribution,
      clinical,
      monthlyTrends,
      engagementMetrics: {
        totalHabitLogs: totalCompetitionPoints,
        averagePointsPerCustomer: customers.length > 0 
          ? Math.round(totalCompetitionPoints / customers.length) 
          : 0,
        activeInLast30Days: activeCustomers
      },
      habitLogsByCategory,
      habitBuildingDistribution
    };
    
    console.log('✅ Final organization statistics:', {
      totalCustomers: stats.totalCustomers,
      activeCustomers: stats.activeCustomers,
      completedExams: stats.completedExams,
      pendingExams: stats.pendingExams,
      averageNamatScore: stats.averageNamatScore,
      totalCompetitionPoints: stats.totalCompetitionPoints,
      monthlyTrendsLength: stats.monthlyTrends.length,
      hasHealthData: Object.values(stats.healthDistribution).some(v => v > 0),
      hasHabitData: Object.values(stats.habitLogsByCategory).some(v => v > 0),
      habitLogsByCategory: stats.habitLogsByCategory,
      hasHabitBuildingData: Object.values(stats.habitBuildingDistribution).some(v => v > 0),
      habitBuildingDistribution: stats.habitBuildingDistribution
    });
    return stats;
    
  } catch (error) {
    console.error('Error fetching organization statistics:', error);
    throw error;
  }
}

